import { Base } from '@studiometa/js-toolkit';

/**
 * AnimatedBlobs component that creates two blobs that move randomly.
 */
export default class AnimatedBlobs extends Base {
  static config = {
    name: 'AnimatedBlobs',
    refs: ['blob1', 'blob2'],
    options: {
      animationDuration: {
        type: Number,
        default: 8000,
      },
    },
  };

  // -------------------------------------------------------------------------
  // Constants
  // -------------------------------------------------------------------------

  /** @type {number} Mobile breakpoint width */
  static MOBILE_BREAKPOINT = 1024;

  /** @type {number} Blob size on mobile */
  static BLOB_SIZE_MOBILE = 600;

  /** @type {number} Blob size on desktop */
  static BLOB_SIZE_DESKTOP = 1200;

  /** @type {number} Maximum offset percentage (50% can be outside viewport) */
  static MAX_OFFSET_RATIO = 0.5;

  /** @type {number} Random duration variation in milliseconds */
  static DURATION_VARIATION = 4000;

  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------

  /** @type {number[]} Animation frames for both blobs */
  animationFrames = [];

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  /**
   * Mounted hook.
   */
  mounted() {
    this.initBlobs();
  }

  /**
   * Destroyed hook.
   */
  destroyed() {
    this.animationFrames.forEach((frame) => cancelAnimationFrame(frame));
  }

  /**
   * Handle resize events.
   */
  resized() {
    this.animationFrames.forEach((frame) => cancelAnimationFrame(frame));
    this.animationFrames = [];
    this.initBlobs();
  }

  // -------------------------------------------------------------------------
  // Methods
  // -------------------------------------------------------------------------

  /**
   * Initialize both blobs.
   */
  initBlobs() {
    const blob1 = /** @type {HTMLElement} */ (this.$refs.blob1);
    const blob2 = /** @type {HTMLElement} */ (this.$refs.blob2);

    if (blob1) {
      this.animateBlob(blob1, 0);
    }

    if (blob2) {
      this.animateBlob(blob2, 1);
    }
  }

  /**
   * Get viewport dimensions.
   * @returns {{ width: number, height: number }}
   */
  getViewportDimensions() {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  }

  /**
   * Get blob size based on viewport width.
   * @param {number} width Viewport width
   * @returns {number} Blob size
   */
  getBlobSize(width) {
    return width < AnimatedBlobs.MOBILE_BREAKPOINT
      ? AnimatedBlobs.BLOB_SIZE_MOBILE
      : AnimatedBlobs.BLOB_SIZE_DESKTOP;
  }

  /**
   * Calculate animation bounds.
   * @param {number} viewportWidth Viewport width
   * @param {number} viewportHeight Viewport height
   * @param {number} blobSize Blob size
   * @returns {{ minX: number, maxX: number, minY: number, maxY: number }}
   */
  calculateBounds(viewportWidth, viewportHeight, blobSize) {
    const maxOffset = blobSize * AnimatedBlobs.MAX_OFFSET_RATIO;

    return {
      minX: -maxOffset,
      maxX: viewportWidth - maxOffset,
      minY: -maxOffset,
      maxY: viewportHeight - maxOffset,
    };
  }

  /**
   * Get initial position for a blob.
   * @param {number} index Blob index (0 or 1)
   * @param {{ minX: number, maxX: number, minY: number, maxY: number }} bounds Animation bounds
   * @returns {{ x: number, y: number }}
   */
  getInitialPosition(index, bounds) {
    const { minX, maxX, minY, maxY } = bounds;
    const rangeX = maxX - minX;
    const rangeY = maxY - minY;

    if (index === 0) {
      return {
        x: minX + Math.random() * rangeX * 0.5,
        y: minY + Math.random() * rangeY,
      };
    }

    return {
      x: minX + rangeX * 0.5 + Math.random() * rangeX * 0.5,
      y: minY + Math.random() * rangeY,
    };
  }

  /**
   * Get random target position within bounds.
   * @param {{ minX: number, maxX: number, minY: number, maxY: number }} bounds Animation bounds
   * @returns {{ x: number, y: number }}
   */
  getRandomTarget(bounds) {
    const { minX, maxX, minY, maxY } = bounds;
    return {
      x: minX + Math.random() * (maxX - minX),
      y: minY + Math.random() * (maxY - minY),
    };
  }

  /**
   * Ease in-out function for smooth animation.
   * @param {number} progress Animation progress (0 to 1)
   * @returns {number} Eased progress
   */
  easeInOut(progress) {
    return progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;
  }

  /**
   * Animate blob movement.
   * @param {HTMLElement} blob Blob element
   * @param {number} index Blob index
   */
  animateBlob(blob, index) {
    let { width: viewportWidth, height: viewportHeight } = this.getViewportDimensions();
    const blobSize = this.getBlobSize(viewportWidth);
    const bounds = this.calculateBounds(viewportWidth, viewportHeight, blobSize);

    let { x: currentX, y: currentY } = this.getInitialPosition(index, bounds);
    let { x: targetX, y: targetY } = this.getRandomTarget(bounds);

    let startTime = null;
    const baseDuration = /** @type {number} */ (this.$options.animationDuration);
    const duration = baseDuration + Math.random() * AnimatedBlobs.DURATION_VARIATION;

    const animate = (timestamp) => {
      const dimensions = this.getViewportDimensions();
      viewportWidth = dimensions.width;
      viewportHeight = dimensions.height;

      const currentBlobSize = this.getBlobSize(viewportWidth);
      const currentBounds = this.calculateBounds(viewportWidth, viewportHeight, currentBlobSize);

      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = this.easeInOut(progress);

      let x = currentX + (targetX - currentX) * eased;
      let y = currentY + (targetY - currentY) * eased;

      x = Math.max(currentBounds.minX, Math.min(x, currentBounds.maxX));
      y = Math.max(currentBounds.minY, Math.min(y, currentBounds.maxY));

      blob.style.transform = `translate(${x}px, ${y}px)`;

      if (progress >= 1) {
        currentX = x;
        currentY = y;
        const newTarget = this.getRandomTarget(currentBounds);
        targetX = newTarget.x;
        targetY = newTarget.y;
        startTime = null;
      }

      this.animationFrames[index] = requestAnimationFrame(animate);
    };

    this.animationFrames[index] = requestAnimationFrame(animate);
  }
}
